import React, { useEffect, useState } from "react";
import { Col, Button } from "reactstrap";
import { defaultTableOption, rowPerPage } from "constants/table";
import { ATTENDANCE_STATUS_OPTIONS } from "constants/status";
import CustomDataTable from "components/customs/table";
import DeclineModal from "components/customs/declineModal";
import SearchBarComponent from "components/customs/searchbarV2";
import { ArrowLeft, FileText } from "react-feather";
import {
  getFormattedDateString,
  formatTime,
  showDeleteModal,
} from "utility/helpers";
import { Link, useLocation, useParams } from "react-router-dom";
import { genericGet, genericPost, genericPut } from "services/services.general";
import { fetchEventReport } from "services/services.events";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import usePersistentCountdownTimer from "hooks/usePersistentTimer";
import { faCommentDollar } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import styled from "styled-components";
import { HeaderText, AddButton } from "assets/js/Styles";
import CommonModal from "components/common/modal";
import eventAttendanceLogColumns from "./columns";
import AttendanceStatistics from "./AttendanceStatistics";
import attendanceColumns from "./attendanceColumns";

const EventDescription = styled.p`
  font-size: 16px;
  word-break: break-word;
  margin-bottom: 3rem;
  margin-top: 1.5rem;
`;

const EventDetailsLeftSection = styled(Col)`
  display: flex;
  flex-direction: column;
`;

const EventDetails = styled.p`
  font-size: 18px;
  margin-bottom: 1rem;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 576px) {
    font-size: 14px;
  }

  @media (max-width: 375px) {
    font-size: 12px;
  }
`;

const EventTitle = styled.h1`
  font-size: 48px;
  margin-bottom: 1rem;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 576px) {
    font-size: 28px;
  }

  @media (max-width: 375px) {
    font-size: 24px;
  }
`;

const TableSection = styled.div`
  margin-top: 16px;
`;

const SearchbarWrapper = styled.div`
  width: 100%;
  position: relative;
  justify-self: flex-end;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 3fr auto auto auto;
  margin-top: 16px;
  gap: 10px;
  align-items: center;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const ExportButton = styled(AddButton)`
  width: 130px;
  background: ${({ theme }) => theme.colors.buttonBlue};
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: 1265px) {
    width: 100%;
  }
`;

const BumpButton = styled(AddButton)`
  width: 130px;
  background: ${({ theme }) => theme.colors.buttonBlue};

  @media (max-width: 1265px) {
    width: 100%;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 50px;
`;

const GoBackButton = styled(Button)`
  margin-right: 16px;
`;

const EventDetailsSection = styled.div`
  margin-top: 44px;
  justify-content: space-between;
  display: flex;
`;

const BumpButtonText = styled.span`
  padding-left: 8px;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.63px;
`;

const AttendanceModalContainer = styled.div`
  height: 70vh;
  overflow-y: scroll;
`;

const ExportButtonText = styled(BumpButtonText)``;

const EventDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const row = location.state.event;

  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });

  const [isDeclineModalShown, setIsDeclineModalShown] = useState(false);
  const [isAttendanceModalShown, setIsAttendanceModalShown] = useState(false);

  const [congEvent, setCongEvent] = useState(null);
  const [eveId, setEventId] = useState(0);

  const [attendanceData, setAttendanceData] = useState(null);

  const [attendanceStats, setAttendanceStats] = useState({
    invitedAttended: 0,
    invitedAttendedByProxy: 0,
    notInvitedAttended: 0,
    notInvitedAttendedByProxy: 0,
  });

  const toggleDeclineModal = () => setIsDeclineModalShown(!isDeclineModalShown);

  const toggleAttendanceModal = () =>
    setIsAttendanceModalShown(!isAttendanceModalShown);

  const { timer, toggleTimer } = usePersistentCountdownTimer();

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  let changeSearchTimeoutID;

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const fetchEventInvitees = () => {
    genericGet({
      url: `/events/${row.id}/participants`,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        search: tableOption.searchValue,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data.rows,
          totalRows: data.count,
        });
      },
    });
  };

  const handleCloseModal = () => {
    toggleDeclineModal();
  };

  const handleCloseAttendanceModal = () => {
    toggleAttendanceModal();
  };

  const handleSaveStatusChange = (data) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    genericPut({
      url: `/eventsparticipant/${data.id}`,
      formData: {
        eventParticipantStatus: data?.eventParticipantStatus,
      },
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          fetchEventInvitees();
        }
      },
    });
  };

  const handleStatusChange = async (index, option) => {
    const newData = [...tableOption.data];
    const oldValue = newData[index].eventParticipantStatus;
    const newValue = option.value;

    const oldStatus = ATTENDANCE_STATUS_OPTIONS.find(
      (status) => status.value === newData[index].eventParticipantStatus
    ).label;

    if (oldValue.toLowerCase() !== newValue.toLowerCase()) {
      try {
        if (newValue === "Declined") {
          newData[index] = {
            ...newData[index], // Keep all other properties
            eventParticipantStatus: newValue,
          };
          setEventId(newData[index].eventId);
          setCongEvent(newData[index]);
          setIsDeclineModalShown(true);
        } else {
          const result = await Swal.fire({
            title: "Warning",
            // eslint-disable-next-line max-len
            text: `Are you sure you want to change the status from ${oldStatus} to ${option.label}?`,
            icon: "warning",
            confirmButtonText: "OK",
            showCancelButton: true,
          });

          if (result.isConfirmed) {
            newData[index] = {
              ...newData[index],
              eventParticipantStatus: newValue,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });

            handleSaveStatusChange(newData[index], index);
          } else {
            newData[index] = {
              ...newData[index],
              eventParticipantStatus: oldValue,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  const handleAttendanceModal = (eventParticipantId) => {
    genericGet({
      // eslint-disable-next-line max-len, prefer-template
      url: `eventsparticipant/${eventParticipantId}/attendance?limit=&offset=&column=&order=`,
      callback: (data) => {
        setAttendanceData(data.rows);

        setIsAttendanceModalShown(true);
      },
    });
  };

  const handleDeleteAction = (rowData) => {
    showDeleteModal({
      title: `participant ${rowData?.Congressman.fullName}`,
      url: `/eventsparticipant/${rowData.id}`,
      callBack: () => {
        fetchEventInvitees();
      },
    });
  };

  const handleDownload = () => {
    try {
      fetchEventReport({
        data: { eventId: id },
        callback: ({ isSuccess, data, filename }) => {
          if (!isSuccess || !data) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch report");

            return;
          }

          const blob = new Blob([data.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Download failed:", error);
    }
  };

  const handleBumpIconOnClicked = async (congId, name) => {
    const result = await Swal.fire({
      title: "Warning",
      text: "Would you like to bump this event to the invitee?",
      icon: "warning",
      confirmButtonText: "Yes",
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      setTableOption({
        ...tableOption,
        isLoading: true,
      });

      toggleTimer(congId);

      genericPost({
        url: "/comment",
        formData: {
          text: `bumped this event to ${name}.`,
          recipientId: null,
          newsId: null,
          eventId: id,
        },
        callback: () => {
          //
        },
      });

      genericPost({
        url: "/events/bulk-bump-sms",
        formData: {
          eventId: id,
          participantIds: [congId],
        },
        callback: () => {
          //
        },
      });
    }
  };

  const handleBulkBumpOnClicked = async () => {
    const isBulkBumpDisabled =
      timer && timer?.find((time) => time?.id === id && time?.isActive);

    if (!isBulkBumpDisabled) {
      const result = await Swal.fire({
        title: "Warning",
        text: "Would you like to bump this event?",
        icon: "warning",
        confirmButtonText: "Yes",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        setTableOption({
          ...tableOption,
          isLoading: true,
        });

        toggleTimer(id);

        genericPost({
          url: "/comment",
          formData: {
            text: "bulk bumped this event.",
            recipientId: null,
            newsId: null,
            eventId: id,
          },
          callback: () => {
            //
          },
        });

        genericPost({
          url: "/events/bulk-bump-sms",
          formData: {
            eventId: id,
          },
          callback: ({ isSuccess }) => {
            if (isSuccess) {
              fetchEventInvitees();
            }
          },
        });
      }
    }
  };

  const getAttendanceStatistics = () => {
    genericGet({
      url: `/eventattendance/${id}/statistics`,
      callback: (data) => {
        if (data?.status && data?.data) {
          setAttendanceStats({
            invitedAttended: data.data.invitedAttended || 0,
            invitedAttendedByProxy: data.data.invitedAttendedByProxy || 0,
            notInvitedAttended: data.data.notInvitedAttended || 0,
            notInvitedAttendedByProxy: data.data.notInvitedAttendedByProxy || 0,
          });
        }
      },
    });
  };

  const columns = eventAttendanceLogColumns({
    handleStatusChange,
    handleAttendanceModal,
    handleDeleteAction,
    handleBumpIconOnClicked,
    timer,
  });

  const renderBody = (
    <AttendanceModalContainer>
      <CustomDataTable
        columns={attendanceColumns()}
        data={attendanceData}
        noHeader
        pagination={false}
      />
    </AttendanceModalContainer>
  );

  useEffect(() => {
    fetchEventInvitees();
    getAttendanceStatistics();
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.sort.direction,
    tableOption.sort.field,
    tableOption.searchValue,
  ]);

  return (
    <>
      <HeaderSection>
        <GoBackButton
          color="primary"
          outline
        >
          <Link
            to="/events"
            state={{
              view: "CongCos",
            }}
          >
            <ArrowLeft />
          </Link>
        </GoBackButton>
        <HeaderText>{"Events"}</HeaderText>
      </HeaderSection>

      <EventDetailsSection>
        <EventDetailsLeftSection
          xs="12"
          md="12"
          lg="12"
          xl="8"
        >
          <EventTitle>{row.eventName}</EventTitle>

          <EventDetails>
            {getFormattedDateString(row.eventStartDate, "MMM dd, yyyy")}{" "}
            {formatTime(row.eventStartTime)}
            {" | "}
            {row.eventVenue}
          </EventDetails>

          {row.eventDescription !== "" && (
            <EventDescription>{row.eventDescription}</EventDescription>
          )}
        </EventDetailsLeftSection>
        <Col
          xs="12"
          md="12"
          lg="12"
          xl="4"
        >
          <AttendanceStatistics
            attendanceStats={attendanceStats}
            totalRows={tableOption.totalRows}
          />
        </Col>
      </EventDetailsSection>

      <TableHeader>
        <h3>{"Attendance Log"} </h3>

        <SearchbarWrapper>
          <SearchBarComponent
            onChangeSearch={onChangeSearch}
            isDisabled={tableOption?.isLoading}
          />
        </SearchbarWrapper>

        <BumpButton onClick={() => handleBulkBumpOnClicked()}>
          <FontAwesomeIcon
            icon={faCommentDollar}
            size="xl"
          />
          <BumpButtonText>{"Bump"}</BumpButtonText>
        </BumpButton>

        <ExportButton
          onClick={handleDownload}
          outline
        >
          <FileText size={24} />
          <ExportButtonText>{"Export"}</ExportButtonText>
        </ExportButton>
      </TableHeader>
      <TableSection>
        <CustomDataTable
          columns={columns}
          data={tableOption.data}
          noHeader
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onSort={onSort}
          paginationPerPage={tableOption.currentRowsPerPage}
          paginationServer
          paginationTotalRows={tableOption.totalRows}
          height="calc(100vh - 200px)"
        />
      </TableSection>

      <DeclineModal
        isOpen={isDeclineModalShown}
        event={congEvent}
        handleClose={handleCloseModal}
        fetchDataTable={fetchEventInvitees}
        eventName={row.eventName}
        eventId={eveId}
        view="events"
      />
      <CommonModal
        isOpen={isAttendanceModalShown}
        title="Days Attended"
        renderBody={renderBody}
        handleClose={handleCloseAttendanceModal}
      />
    </>
  );
};

export default EventDetail;
